import { UserName } from "./UserName.tsx";
import { useEffect, useState } from "react";
import { MAX_ATTEMPTS, type DailyFriendEntry, type DailyFriendStatus } from "@mk/shared";
import { api } from "../lib/api.ts";
import { ResultBoard } from "./ResultBoard.tsx";
import { Sheet } from "./Sheet.tsx";
import { messages } from "../i18n/index.ts";

interface DailyFriendsSheetProps {
  open: boolean;
  onClose: () => void;
}

const STATUS_TEXT: Record<DailyFriendStatus, string> = messages.dailyFriends.status;

/**
 * Arkadaşların bugünkü günlük kelimedeki durumu.
 *
 * Satıra basınca tahtası açılır — ama yalnızca oyunu bitmiş arkadaşlarınki.
 * Hâlâ oynayanın tahtası paylaşılmaz: devam eden bir oyuna bakmak ve dışarıdan
 * ipucu vermek mümkün olmasın.
 *
 * Panel zaten sen bugünü bitirdikten sonra açılabildiği için kendi oyununu
 * bozmuyor.
 */
export function DailyFriendsSheet({ open, onClose }: DailyFriendsSheetProps) {
  const [entries, setEntries] = useState<DailyFriendEntry[] | null>(null);
  const [error, setError] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setEntries(null);
    setError(false);
    setOpenId(null);
    api<{ entries: DailyFriendEntry[] }>("/api/solo/daily/friends")
      .then((data) => {
        if (!cancelled) setEntries(data.entries);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  return (
    <Sheet open={open} title={messages.dailyFriends.title} placement="center" onClose={onClose}>
      {error && <p className="text-sm text-muted">{messages.dailyFriends.loadFailed}</p>}

      {!error && entries === null && <p className="text-sm text-muted">{messages.common.loading}</p>}

      {!error && entries?.length === 0 && (
        <p className="text-sm leading-relaxed text-muted">
          {messages.dailyFriends.empty}
        </p>
      )}

      {entries && entries.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {entries.map((entry) => {
            const canOpen = entry.guesses.length > 0;
            const expanded = openId === entry.user.id;
            return (
              <li key={entry.user.id}>
                <button
                  type="button"
                  className="daily-friend"
                  data-status={entry.status}
                  data-expanded={expanded ? "true" : undefined}
                  disabled={!canOpen}
                  aria-expanded={canOpen ? expanded : undefined}
                  onClick={() => setOpenId(expanded ? null : entry.user.id)}
                >
                  <span className="daily-friend__score">
                    {entry.status === "solved" ? entry.attempts : "—"}
                  </span>
                  <span className="daily-friend__name">{<UserName user={entry.user} />}</span>
                  <span className="daily-friend__status">
                    {entry.status === "solved"
                      ? `${entry.attempts}/${MAX_ATTEMPTS}`
                      : STATUS_TEXT[entry.status]}
                  </span>
                  {/* Yer her satırda ayrılıyor ki durum sütunu hizada kalsın. */}
                  <span className="daily-friend__chevron" aria-hidden="true">
                    {canOpen ? "›" : ""}
                  </span>
                </button>

                {expanded && (
                  <div className="daily-friend__board">
                    <ResultBoard rows={entry.guesses} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <button type="button" className="btn-secondary mt-5 w-full" onClick={onClose}>
        {messages.common.close}
      </button>
    </Sheet>
  );
}
